import { Button } from "@/components/ui/button";
import { Plus, LogOut, Filter, Users, Search, Sun, Moon, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState, useEffect, lazy, Suspense } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import CollaboratorsDialog from "./CollaboratorsDialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import QuickAddTask from "./QuickAddTask";
import { SortOption } from "@/types/task";

// Lazy load FilterDialog
const FilterDialog = lazy(() => import("./FilterDialog"));

interface TaskMatrixHeaderProps {
  onAddTask: () => void;
  onFilterChange: (filters: { search: string; tags: string[]; sortBy?: SortOption }) => void;
  availableTags: string[];
  filters: { search: string; tags: string[]; sortBy?: SortOption };
}

const TaskMatrixHeader = ({ onAddTask, onFilterChange, availableTags, filters }: TaskMatrixHeaderProps) => {
  const navigate = useNavigate();
  const { user, isEmailVerified } = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(false);
  const isMobile = useIsMobile();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState(filters.search ?? "");
  const [quickAddQuadrant, setQuickAddQuadrant] = useState<{ importance: number; urgency: number }>({ importance: 1, urgency: 1 });
  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = localStorage.getItem("theme");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return document.documentElement.classList.contains("dark");
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  useEffect(() => {
    const id = setTimeout(() => {
      onFilterChange({ search: searchValue, tags: filters.tags, sortBy: filters.sortBy });
    }, 250);
    return () => clearTimeout(id);
  }, [searchValue, onFilterChange, filters.tags, filters.sortBy]);

  useEffect(() => {
    // Keep search input in sync when external filters change (e.g., FilterDialog)
    setSearchValue(filters.search ?? "");
  }, [filters.search]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Error logging out");
    } else {
      toast.success("Logged out successfully");
      navigate("/auth");
    }
  };

  const handleAddTaskClick = () => {
    if (!isEmailVerified) {
      toast.error("Please verify your email before adding tasks");
      return;
    }
    onAddTask();
  };

  const getUserInitials = () => {
    const email = user?.email;
    if (!email) return "?";
    return email.substring(0, 2).toUpperCase();
  };

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
  };

  return (
    <>
      <div className="bg-gradient-to-r from-white/90 via-indigo-50/50 to-purple-50/30 backdrop-blur-xl border-b border-white/20 px-4 sm:px-6 py-6 sm:py-8 mb-6 sm:mb-8 shadow-soft">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent leading-tight">
                taskvista
              </h1>
              <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle theme" className="h-9 w-9">
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
            {!isEmailVerified && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse-soft"></div>
                <span className="text-xs text-amber-700 font-medium bg-amber-100/70 py-1.5 px-3 rounded-full border border-amber-200/50">
                  Email verification pending
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 w-full sm:w-[360px]">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search tasks"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="pl-8"
                  aria-label="Search tasks"
                />
                {searchValue && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearchValue("")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <select
                  className="h-9 rounded-md border border-border bg-white/70 px-2 text-sm"
                  value={`${quickAddQuadrant.importance}-${quickAddQuadrant.urgency}`}
                  onChange={(e) => {
                    const [i, u] = e.target.value.split('-').map(Number);
                    setQuickAddQuadrant({ importance: i, urgency: u });
                  }}
                  aria-label="Quick add quadrant"
                >
                  <option value="1-1">Do first</option>
                  <option value="1-0">Schedule</option>
                  <option value="0-1">Delegate</option>
                  <option value="0-0">Eliminate</option>
                </select>
                <div className="w-[220px]">
                  <QuickAddTask importance={quickAddQuadrant.importance} urgency={quickAddQuadrant.urgency} disabled={!isEmailVerified} />
                </div>
              </div>
            </div>
            <p className="text-slate-600 text-sm font-medium">
              Organize • Prioritize • Achieve
            </p>
          </div>
          
          <div className="flex items-center flex-wrap gap-3 w-full sm:w-auto justify-start sm:justify-end">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button 
                    size={isMobile ? "sm" : "default"}
                    className={`${isEmailVerified 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-colored-important' 
                      : 'bg-indigo-400'
                    } text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 hover:-translate-y-0.5 interactive`}
                    onClick={handleAddTaskClick}
                    disabled={!isEmailVerified}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span className="text-sm">Add Task</span>
                  </Button>
                </div>
              </TooltipTrigger>
              {!isEmailVerified && (
                <TooltipContent>
                  <p>Verify your email to add tasks</p>
                </TooltipContent>
              )}
            </Tooltip>
            
            <Button 
              size={isMobile ? "sm" : "default"}
              variant="outline" 
              className="border-white/30 bg-white/50 backdrop-blur-sm hover:bg-white/70 shadow-soft rounded-xl px-4 py-3 transition-all duration-300 hover:-translate-y-0.5"
              onClick={() => setIsFilterOpen(true)}
            >
              <Filter className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only sm:ml-2 sm:text-sm font-medium">Filter</span>
            </Button>
            
            <Button 
              size={isMobile ? "sm" : "default"}
              variant="outline"
              className="border-white/30 bg-white/50 backdrop-blur-sm hover:bg-white/70 shadow-soft rounded-xl px-4 py-3 transition-all duration-300 hover:-translate-y-0.5"
              onClick={() => setIsCollaboratorsOpen(true)}
            >
              <Users className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only sm:ml-2 sm:text-sm font-medium">Team</span>
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button aria-label="User menu" className="rounded-full">
                  <Avatar className="h-10 w-10 cursor-pointer hover:opacity-80 transition-all duration-300 hover:scale-105 shadow-soft ring-2 ring-white/50">
                    <AvatarImage src={avatarUrl || ""} alt="Profile" />
                    <AvatarFallback className="bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-800 font-bold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white/90 backdrop-blur-sm border-white/20 shadow-medium rounded-xl">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold">My Account</span>
                    <span className="text-xs text-gray-500 truncate max-w-[200px]">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500 cursor-pointer hover:bg-red-50 rounded-lg">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      <Suspense fallback={<div>Loading...</div>}>
        {isFilterOpen && (
          <FilterDialog
            open={isFilterOpen}
            onOpenChange={setIsFilterOpen}
            onFilterChange={onFilterChange}
            availableTags={availableTags}
          />
        )}
      </Suspense>
      
      <CollaboratorsDialog 
        open={isCollaboratorsOpen}
        onOpenChange={setIsCollaboratorsOpen}
      />
    </>
  );
};

export default TaskMatrixHeader;
